import { useMemo, useState } from 'react';
import { Banner, Button, Skeleton, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import { usePermissionMatrix, useSaveTemplates } from './api';
import { CreateRoleDialog } from './CreateRoleDialog';
import { PermissionMatrix, type MatrixRowView } from './PermissionMatrix';
import styles from './PermissionsPage.module.css';

const INTRO =
  'Jede Rolle startet mit dieser Vorlage. Abweichungen setzen Sie je Nutzer. Die Navigation ist für alle gleich, Rechte regeln, welche Daten jemand sieht.';

/** `roleId → key → value` of the unsaved template changes. */
type Draft = Record<string, Record<string, string>>;

/**
 * Admin 01 „Rechte und Rollen": the templates of all roles (7 system roles + custom roles). A cell opens the
 * allowed values; changed cells carry the accent chip until „Speichern" sends `PUT /roles/:id/permissions` per
 * changed role. „Rolle anlegen" copies a template (CreateRoleDialog).
 */
export function PermissionsPage() {
  const { toast } = useToast();
  const matrix = usePermissionMatrix();
  const save = useSaveTemplates();
  const [draft, setDraft] = useState<Draft>({});
  const [createOpen, setCreateOpen] = useState(false);
  const data = matrix.data;
  const editable = data?.actions.save.allowed ?? false;
  const changeCount = Object.values(draft).reduce((sum, keys) => sum + Object.keys(keys).length, 0);

  const categories = useMemo(() => {
    if (!data) return [];
    return data.categories.map((category) => ({
      label: category.label,
      rows: category.rows.map<MatrixRowView>((row) => ({
        key: row.key,
        label: row.label,
        sensitive: row.sensitive,
        options: row.options,
        cells: row.cells.map((cell) => {
          const changed = draft[cell.roleId]?.[row.key];
          const value = changed ?? cell.value;
          return {
            roleId: cell.roleId,
            value,
            label: row.options.find((option) => option.value === value)?.label ?? cell.label,
            editable,
            highlighted: changed !== undefined,
            lockedReason: editable ? null : data.actions.save.reason,
          };
        }),
      })),
    }));
  }, [data, draft, editable]);

  function change(roleId: string, key: string, value: string) {
    const original = data?.categories
      .flatMap((category) => category.rows)
      .find((row) => row.key === key)
      ?.cells.find((cell) => cell.roleId === roleId)?.value;
    setDraft((current) => {
      const role = { ...current[roleId] };
      if (value === original) delete role[key];
      else role[key] = value;
      const next = { ...current, [roleId]: role };
      if (Object.keys(role).length === 0) delete next[roleId];
      return next;
    });
  }

  function submit() {
    const changes = Object.entries(draft).map(([roleId, keys]) => ({
      roleId,
      changes: Object.entries(keys).map(([key, value]) => ({ key, value })),
    }));
    save.mutate(changes, {
      onSuccess: () => {
        setDraft({});
        toast({ message: 'Vorlagen gespeichert.' });
      },
    });
  }

  return (
    <OrganizationPage
      variant="admin"
      title="Rechte und Rollen"
      description={INTRO}
      actions={
        <>
          {data?.actions.createRole.allowed ? (
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Rolle anlegen
            </Button>
          ) : null}
          <Button disabled={changeCount === 0 || !editable} loading={save.isPending} onClick={submit}>
            Speichern
          </Button>
        </>
      }
    >
      {matrix.isError ? <QueryError error={matrix.error} /> : null}
      {save.isError ? <Banner tone="danger">{errorMessage(save.error)}</Banner> : null}
      {matrix.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Skeleton key={index} variant="block" height={36} />
          ))}
        </div>
      ) : null}
      {data ? <PermissionMatrix roles={data.roles} categories={categories} onChange={change} /> : null}
      {data ? (
        <CreateRoleDialog open={createOpen} roles={data.roles} onClose={() => setCreateOpen(false)} />
      ) : null}
    </OrganizationPage>
  );
}
